import { Directive, HostBinding, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { getFirstError, showError } from '../../validation';
import { Errors, InputErrors } from '../../errors';

@Directive({
    selector: '[nomAbstractInput]',
    standalone: false
})
export abstract class AbstractInputDirective<T = any> {
  protected readonly getFirstError = getFirstError;

  /**
   * The input's ID attribute.
   */
  @Input()
  public inputId = `input-${uuidv4()}`;

   /**
    * The visible name of this form control. Leave blank for no name.
    */
  @Input()
  public name = '';

  /**
   * Whether the visible name of this form control should be bolded.
   */
  @Input()
  public bold = false;

  /**
   * The control to present. Use this OR value, not both.
   */
  @Input()
  public control!: FormControl<T>;

  /**
   * Any errors to display alongside this form control.
   */
  @Input()
  public errors: InputErrors = Errors.defaults;

  /**
   * Whether this field is readonly, i.e. unable to be modified.
   */
  @Input()
  public readonly = false;

  /**
   * Whether the validiation icons will be shown.
   */
    #validate = true;
    @Input()
    public set validation(value: boolean) {
      this.#validate = value;
    }

    public get validation() {
      return this.readonly ? false : this.#validate
    }

  /**
   * Whether to autocomplete the field.
   *
   * This corresponds to the HTML Input Element autocomplete value.
   */
  @Input()
  public autocomplete = 'on';

  @HostBinding('class.has-errors')
  public get hasErrorsClass() { return this.showError; }

  @HostBinding('class.nom-input')
  public readonly nomInputClass = true;

  /**
   * Whether to show an error in the UI for this field.
   * @internal
   */
   public get showError(): boolean {
    return showError(this.control) && !!this.errorPresentation;
  }

  /**
   * Get the presentation for the first error to display in this field.
   * @internal
   */
  public get errorPresentation(): string | null {
    if (!this.control) {
      return null;
    }
    const firstError = this.getFirstError(this.control);
    if (firstError) {
      const errorMessage = this.errors[firstError];
      if (typeof errorMessage === 'function') {
        return errorMessage(this.control.getError(firstError));
      } else {
        return errorMessage ?? null;
      }
    }
    return null;
  }

}
